import { DownloadOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Checkbox, Modal, Select, Tooltip } from 'antd';
import { uniq } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import {
  DashboardName,
  exportOrderOverview,
  exportOrderWastage,
  exportReview,
  exportSaleConvert,
} from 'services/api/dashboard';
import { DashboardFilterType } from './useFilter';
import {
  platformAll2Undefined,
  momentDateRange2FetchParams,
  storeType2FetchParams,
} from '../../utils';
import { useTranslate } from '@tolgee/react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useDashboardCurrency } from '../../hooks/useCurrency';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { DepartmentsSelect } from './DepartmentSelect';
import { DashboardPlatformSelectorFeature, DashboardStoreSelectorFeature } from 'utils/featureFlag';
import {
  mercadoNewDataDate,
  usePlatformSelect,
  useStoreInfoSelect,
  useStoreTypeSelect,
} from './Provider';
import { RegionFixedExchangeRateValue } from 'services/config/region';
import { Scene } from 'services/api/dashboard';
import { currencyOptions } from './CurrencySelector';
import dayjs, { Dayjs } from 'lib/dayjs';
import { DatePicker } from 'components/DatePicker';

type Type = Exclude<DashboardName, 'PerformanceAgent' | 'PerformanceRobot' | 'AIReplyRobotReply'>;

export interface ExportDataProps {
  type: Type;
  scene: Scene;
  dashboardName: DashboardName;
  showPlatformAll?: boolean;
  initialValue: DashboardFilterType;
}

function ExportData(props: ExportDataProps & { currency: RegionFixedExchangeRateValue }) {
  const { type, currency, scene, dashboardName, showPlatformAll, initialValue } = props;

  const { t } = useTranslate();

  const lang = useSystemLanguageValue();

  const [filter, setFilter] = useState({ ...initialValue, currency });

  const typeConfigMap = useMemo(() => {
    return {
      OrderOverview: [
        ['数据总览'],
        ['非COD每日订单变化', 'COD每日订单变化'],
        ['COD、非COD下单情况', 'COD、非COD付款情况'],
        ['静默、非静默单情况'],
        ['弃单情况', '复购情况'],
      ],
      SalesConversion: [
        ['售前咨询转化', '整体咨询转化'],
        ['催单转化', '非COD催付转化'],
      ],
      OrderLoss: [
        ['非COD订单损耗过程分析', 'COD订单损耗过程分析'],
        ['非COD订单损耗归因分析-损耗明细', '非COD订单损耗归因分析-天级损耗'],
        ['COD订单损耗归因分析-损耗明细', 'COD订单损耗归因分析-天级损耗'],
      ],
      Review: [
        ['买家评价', '回评数据'],
        ['自动催好评', '中差评挽回'],
      ],
    } as const;
  }, []);

  const optionLabelMap = useMemo<Record<Util.ElementType<typeof allOptions>, string>>(() => {
    return {
      数据总览: t('data_overview'),
      非COD每日订单变化: t('ViewDashboard.非COD每日盈利变化'),
      COD每日订单变化: t('ViewDashboard.COD每日盈利变化'),
      'COD、非COD下单情况': t('ViewDashboard.COD非COD下单情况'),
      'COD、非COD付款情况': t('ViewDashboard.COD非COD付款情况'),
      '静默、非静默单情况': t('ViewDashboard.静默非静默单情况'),
      弃单情况: t('aide.弃单情况'),
      复购情况: t('ViewDashboard.复购情况'),
      售前咨询转化: t('ViewDashboard.售前咨询转化'),
      整体咨询转化: t('ViewDashboard.整体咨询转化'),
      催单转化: t('ViewDashboard.催单转化'),
      非COD催付转化: t('ViewDashboard.非COD催付转化'),
      非COD订单损耗过程分析: t('ViewDashboard.非COD订单损耗情况'),
      COD订单损耗过程分析: t('ViewDashboard.COD订单损耗情况'),
      '非COD订单损耗归因分析-损耗明细': `${t('ViewDashboard.非COD订单损耗归因分析')}-${t(
        'ViewDashboard.数据表格',
      )}`,
      '非COD订单损耗归因分析-天级损耗': `${t('ViewDashboard.非COD订单损耗归因分析')}-${t(
        'ViewDashboard.统计图表',
      )}`,
      'COD订单损耗归因分析-损耗明细': `${t('ViewDashboard.COD订单损耗归因分析')}-${t(
        'ViewDashboard.数据表格',
      )}`,
      'COD订单损耗归因分析-天级损耗': `${t('ViewDashboard.COD订单损耗归因分析')}-${t(
        'ViewDashboard.统计图表',
      )}`,
      买家评价: t('ViewDashboard.买家评价'),
      回评数据: t('ViewDashboard.回评数据'),
      自动催好评: t('ViewDashboard.自动催好评'),
      中差评挽回: t('ViewDashboard.中差评挽回'),
    };
  }, [t]);

  const [visible, setVisible] = useState(false);

  const allOptions = useMemo(() => typeConfigMap[type].flat(), [typeConfigMap, type]);

  const [checkedOptions, setCheckedOptions] = useState(allOptions);
  const { run: onDownload, loading: exporting } = useExportFile();

  const allChecked = checkedOptions.length === allOptions.length;

  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    departments: filter.departments,
    scene,
    dashboardName,
    showPlatformAll,
    value: filter.platform,
    onChange: (value) => {
      setFilter((pre) => {
        const dateRange = pre.dateRange;
        const isMercadoFlag =
          dashboardName === 'OrderLoss' &&
          value === 'MERCADO' &&
          dateRange &&
          dateRange[0].isBefore(mercadoNewDataDate, 'days') &&
          dateRange[1].isSameOrAfter(mercadoNewDataDate, 'days');

        return {
          ...pre,
          platform: value,
          storeInfo: {},
          dateRange: isMercadoFlag ? [dayjs(mercadoNewDataDate), dateRange[1]] : dateRange,
        };
      });
    },
  });

  const { storeTypeSelect } = useStoreTypeSelect({
    value: filter.storeType,
    onChange: (storeType) => {
      setFilter((pre) => ({
        ...pre,
        storeType,
        storeInfo: {},
      }));
    },
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    regionInfo,
    platform: filter.platform,
    departments: filter.departments,
    platformOptions,
    storeType: filter.storeType,
    storeInfo: filter.storeInfo,
    onChange: (storeInfo) =>
      setFilter((pre) => ({
        ...pre,
        storeInfo,
      })),
    scene,
  });

  const modal = (
    <Modal
      visible={visible}
      width={720}
      title={
        <div>
          {t('export_data')}
          <span className="text-xs text-[#84868C] font-normal ml-2">{t('导出数据提示')}</span>
        </div>
      }
      onCancel={() => setVisible(false)}
      footer={[
        <span style={{ marginRight: '12px', color: '#84868C' }}>
          {t('aide.已选x项', { x: checkedOptions.length })}
        </span>,
        <Button onClick={() => setVisible(false)}>{t('translation.取消')}</Button>,
        <Button
          type="primary"
          disabled={checkedOptions.length === 0}
          onClick={async () => {
            onDownload(() => {
              setVisible(false);

              const downloadApi = {
                OrderOverview: exportOrderOverview,
                SalesConversion: exportSaleConvert,
                OrderLoss: exportOrderWastage,
                Review: exportReview,
              }[type];

              const {
                dateRange,
                platform,
                storeType,
                storeInfo: { list = [] },
                departments,
                currency,
              } = filter;
              return downloadApi({
                lang,
                currency: currency.shorthand,
                ...momentDateRange2FetchParams(dateRange),
                platform: platformAll2Undefined(platform),
                storeType: storeType2FetchParams(storeType),
                storeIds: list.map((x) => x.id),
                departmentIds: departments?.map((_) => _.id),
                indexes: checkedOptions,
              });
            });
          }}
        >
          {t('review.导出')}
        </Button>,
      ]}
    >
      <Content>
        <header className="flex gap-4 flex-wrap !py-[8px]">
          <DepartmentsSelect
            dashboardType="order"
            value={filter.departments}
            onChange={(departments) => {
              setFilter((pre) => ({
                ...pre,
                departments,
                platform: undefined,
                storeInfo: {},
              }));
            }}
          />
          {DashboardPlatformSelectorFeature && platformSelect}
          {DashboardStoreSelectorFeature && storeTypeSelect}
          {storeInfoSelect}
          <Select
            value={filter.currency.region}
            options={currencyOptions}
            onChange={(value) => {
              const currency = currencyOptions.find((_) => _.region === value);
              setFilter((pre) => ({
                ...pre,
                currency: currency!,
              }));
            }}
          />
        </header>
        <section className="flex gap-4 items-center">
          <span>{t('ViewDashboard.日期')}</span>
          <DatePicker.RangePicker
            className="w-[380px]"
            disabledDate={(current) => {
              return current && (current < dayjs().subtract(1, 'year') || current > dayjs());
            }}
            value={filter.dateRange}
            onChange={(dateRange) => {
              setFilter((pre) => ({ ...pre, dateRange: dateRange as [Dayjs, Dayjs] }));
            }}
          />
        </section>
      </Content>
      <Content className="mt-4">
        <header>
          <Checkbox
            checked={allChecked}
            indeterminate={!allChecked && checkedOptions.length > 0}
            onChange={({ target: { checked } }) => setCheckedOptions(checked ? allOptions : [])}
          >
            {t('review.全部')}
          </Checkbox>
        </header>
        <section>
          {typeConfigMap[type].map((options) => {
            return (
              <div key={options[0]}>
                {options.map((option) => {
                  return (
                    <Checkbox
                      key={option}
                      checked={checkedOptions.includes(option)}
                      onChange={({ target: { checked } }) =>
                        setCheckedOptions((pre) =>
                          checked ? uniq([...pre, option]) : pre.filter((_) => _ !== option),
                        )
                      }
                    >
                      {optionLabelMap[option]}
                    </Checkbox>
                  );
                })}
              </div>
            );
          })}
        </section>
      </Content>
    </Modal>
  );

  const button = (
    <Tooltip title={t('export_data')}>
      <Button loading={exporting} icon={<DownloadOutlined />} onClick={() => setVisible(true)} />
    </Tooltip>
  );

  useEffect(() => {
    if (visible) {
      setFilter({
        ...initialValue,
        currency,
      });
    }
  }, [currency, initialValue, visible]);

  return (
    <>
      {button}
      {modal}
    </>
  );
}

const Content = styled.div`
  border: solid 1px #f0f2f5;
  border-radius: 2px;
  > header {
    display: flex;
    align-items: center;
    min-height: 46px;
    padding: 0 16px;
    border-bottom: solid 1px #f0f2f5;
  }
  > section {
    padding: 16px 24px;
    > div {
      display: flex;
      & + div {
        margin-top: 12px;
      }
      > * {
        flex: 1 0 0;
      }
    }
  }
`;

export default function ExportDataWrapper(props: ExportDataProps) {
  const { currency } = useDashboardCurrency();

  if (!currency) {
    return null;
  }

  return <ExportData {...props} currency={currency} />;
}
